import React, { useEffect } from "react";
import { User } from "../states/users/UsersSlice";
import "../pages/dashboard/Dashboard.css";

interface EditUserModalProps {
  isOpen: boolean;
  user: User | null;
  onClose: () => void;
  onSave: (data: {
    name: string;
    userName?: string;
    email?: string;
    password: string;
    profiles: any[];
  }) => void;
}

const EditUserModal: React.FC<EditUserModalProps> = ({
  isOpen,
  user,
  onClose,
  onSave,
}) => {
  const [password, setPassword] = React.useState("");
  const [name, setName] = React.useState(user?.name || "");
  const [userName, setUserName] = React.useState(user?.userName || "");
  const [email, setEmail] = React.useState(user?.email || "");
  const [profiles, setProfiles] = React.useState(user?.profile || []);

  useEffect(() => {
    setName(user?.name || "");
    setUserName(user?.userName || "");
    setEmail(user?.email || "");
    setProfiles(user?.profile || []);
    setPassword("");
  }, [user]);

  // Helper to update a specific profile field
  const updateProfile = (
    profileId: string,
    field: string,
    value: string | number
  ) => {
    setProfiles(
      profiles.map((p) => (p.id === profileId ? { ...p, [field]: value } : p))
    );
  };

  // Handle save with edited data
  const handleSave = () => {
    // Convert date_of_birth to ISO format before sending
    const formattedProfiles = profiles.map((profile) => ({
      ...profile,
      date_of_birth: profile.date_of_birth.includes("T")
        ? profile.date_of_birth
        : new Date(profile.date_of_birth).toISOString(),
    }));

    onSave({
      name,
      userName,
      email,
      password,
      profiles: formattedProfiles,
    });

    // Clear form data after save
    setPassword("");
    setName("");
    setUserName("");
    setEmail("");
    setProfiles([]);
  };

  if (!isOpen) return null;

  const isCreateMode = !user;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            {isCreateMode ? "Create New User" : "Edit User Profile"}
          </h2>
          <button onClick={onClose} className="modal-close-button">
            ×
          </button>
        </div>

        {/* User Info Section */}
        <div className="modal-section">
          <h3 className="modal-section-title">User Information</h3>
          <div className="modal-grid-2">
            <div className="modal-field">
              <label className="modal-label">UserName</label>
              <input
                type="tel"
                className="modal-value"
                placeholder="Enter phone number"
                value={userName}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  setUserName(value);
                }}
              />
            </div>
            <div className="modal-field">
              <label className="modal-label">Password</label>
              <input
                type="password"
                className="modal-value"
                placeholder="Enter new password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="modal-field">
              <label className="modal-label">Name</label>
              <input
                type="text"
                className="modal-value"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="modal-field">
              <label className="modal-label">Email</label>
              <input
                type="email"
                className="modal-value"
                placeholder="Enter email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Profile Section */}
        {profiles && profiles.length > 0 && (
          <div className="modal-section">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <h3 className="modal-section-title">
                Profile Information ({profiles.length} profile
                {profiles.length !== 1 ? "s" : ""})
              </h3>
              <button
                onClick={() => {
                  const newProfile = {
                    id: `temp-${Date.now()}`,
                    profile_name: "",
                    bio: "",
                    avatar: "",
                    date_of_birth: new Date().toISOString().split("T")[0],
                    current_level: 1,
                    current_unit: 1,
                    current_lesson: 1,
                  };
                  setProfiles([...profiles, newProfile]);
                }}
                className="modal-button modal-button-primary"
                style={{ padding: "6px 12px", fontSize: "14px" }}
              >
                + Add Profile
              </button>
            </div>
            {profiles.map((profile, index) => (
              <div
                key={profile.id}
                style={{
                  display: "grid",
                  gap: "12px",
                  marginBottom:
                    index < (profiles?.length || 0) - 1 ? "20px" : "0",
                  paddingBottom:
                    index < (profiles?.length || 0) - 1 ? "20px" : "0",
                  borderBottom:
                    index < (profiles?.length || 0) - 1
                      ? "1px solid #e5e7eb"
                      : "none",
                }}
              >
                {profiles && profiles.length > 1 && (
                  <h4
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#555",
                      marginBottom: "8px",
                    }}
                  >
                    Profile #{index + 1}
                  </h4>
                )}
                <div className="modal-field">
                  <label className="modal-label">Profile Name</label>
                  <input
                    type="text"
                    className="modal-value"
                    value={profile.profile_name}
                    onChange={(e) =>
                      updateProfile(profile.id, "profile_name", e.target.value)
                    }
                  />
                </div>
                <div className="modal-field">
                  <label className="modal-label">Level</label>
                  <select
                    className="modal-value"
                    value={profile.current_level}
                    onChange={(e) =>
                      updateProfile(
                        profile.id,
                        "current_level",
                        parseInt(e.target.value)
                      )
                    }
                  >
                    <option value="1">Level 1</option>
                    <option value="2">Level 2</option>
                    <option value="3">Level 3</option>
                  </select>
                </div>
                <div className="modal-field">
                  <label className="modal-label">Date of Birth</label>
                  <input
                    type="date"
                    className="modal-value"
                    value={profile.date_of_birth.split("T")[0]}
                    onChange={(e) =>
                      updateProfile(profile.id, "date_of_birth", e.target.value)
                    }
                  />
                </div>
                <div className="modal-field">
                  <label className="modal-label">Bio</label>
                  <textarea
                    className="modal-value-bio"
                    value={profile.bio}
                    onChange={(e) =>
                      updateProfile(profile.id, "bio", e.target.value)
                    }
                    rows={3}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {(!profiles || profiles.length === 0) && (
          <div className="modal-no-profile">
            <p>No profile information available for this user.</p>
            <button
              onClick={() => {
                const newProfile = {
                  id: `temp-${Date.now()}`,
                  profile_name: "",
                  bio: "",
                  avatar: "",
                  date_of_birth: new Date().toISOString().split("T")[0],
                  current_level: 1,
                  current_unit: 1,
                  current_lesson: 1,
                };
                setProfiles([newProfile]);
              }}
              className="modal-button modal-button-primary"
              style={{ marginTop: "12px" }}
            >
              + Add Profile
            </button>
          </div>
        )}

        {/* Action Buttons */}
        <div className="modal-actions">
          <button
            onClick={onClose}
            className="modal-button modal-button-secondary"
          >
            Close
          </button>
          <button
            onClick={handleSave}
            className="modal-button modal-button-primary"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditUserModal;
